import { isEmpty } from 'lodash';

import { TileNode } from '@/components/diagrams/FlowChart';
import { useNode } from '@/components/diagrams/FlowChart/hooks/useNode';
import { useResidentialStateForDeath } from '@/modules/taxes/nonResidentialState.utils';
import { AfterDeath, StateCode } from '@/types/schema';

import { EstateWaterfall_TaxSummaryV2Fragment } from '../graphql/EstateWaterfall.generated';
import { useTaxBreakout } from '../hooks/useTaxBreakout';
import { getDiagramTaxTileTitle } from './TaxLineOne.utils';

export interface TaxLineOneProps {
  summary: EstateWaterfall_TaxSummaryV2Fragment;
  afterDeath?: AfterDeath;
  firstGrantorDeathId?: string | null;
}

export const TaxLineOne = ({
  summary,
  afterDeath,
  firstGrantorDeathId,
}: TaxLineOneProps) => {
  const node = useNode<TileNode>();

  const { stateTaxesByCode, inheritanceTaxesByCode, gstTax, federal } =
    useTaxBreakout(summary, { afterDeath, firstGrantorDeathId });

  const residentialStateCode = useResidentialStateForDeath({
    afterDeath: afterDeath ?? AfterDeath.None,
    firstGrantorDeathId,
  });

  const hasInheritanceTax = !isEmpty(inheritanceTaxesByCode);

  const stateCodesWithTax = (
    Object.entries(stateTaxesByCode) as [
      StateCode,
      (typeof stateTaxesByCode)[StateCode],
    ][]
  )
    .filter(([, tax]) => tax.greaterThan(0))
    .map(([stateCode]) => stateCode);

  const residentialStateHasTax =
    residentialStateCode !== undefined &&
    stateCodesWithTax.includes(residentialStateCode);

  const nonResidentialStateCodes = stateCodesWithTax.filter(
    (code) => code !== residentialStateCode
  );

  const inheritancePresent = Object.values(inheritanceTaxesByCode).some((t) =>
    t.greaterThan(0)
  );

  const title = getDiagramTaxTileTitle({
    federal: federal.greaterThan(0),
    residentialStateCode: residentialStateHasTax
      ? (residentialStateCode ?? null)
      : null,
    nonResidentialStateCodes,
    gst: !!gstTax?.greaterThan(0),
    inheritance: inheritancePresent,
  });

  if (title !== null) return <>{title}</>;
  // Fall through to legacy behavior when no enumerated category matches.

  if (node.data.estateWaterfallConfig?.showTaxBreakout) {
    if (hasInheritanceTax && gstTax?.greaterThan(0)) {
      return <>{'Estate, inheritance, & GST tax'}</>;
    }
    if (hasInheritanceTax) {
      return <>{'Estate & inheritance tax'}</>;
    }
    if (gstTax?.greaterThan(0)) {
      return <>{'Estate & GST tax'}</>;
    }
    return <>{'Estate tax'}</>;
  }

  // Legacy: "Federal, IL estate, PA inheritance, & GST tax".
  const parts: string[] = [];
  if (federal.greaterThan(0)) {
    parts.push('Federal');
  }

  // BE returns a row for any situs state; drop $0s from the title.
  const estateStates = Object.entries(stateTaxesByCode)
    .filter(([, tax]) => tax.greaterThan(0))
    .map(([stateCode]) => stateCode);
  if (!isEmpty(estateStates)) {
    parts.push(`${estateStates.join(', ')} estate`);
  }

  const inheritanceStates = Object.entries(inheritanceTaxesByCode)
    .filter(([, tax]) => tax.greaterThan(0))
    .map(([stateCode]) => stateCode);
  if (!isEmpty(inheritanceStates)) {
    parts.push(`${inheritanceStates.join(', ')} inheritance`);
  }

  if (gstTax?.greaterThan(0)) {
    parts.push('GST');
  }

  const lineOne =
    parts.length <= 2
      ? `${parts.join(' & ')} tax`
      : `${parts.slice(0, -1).join(', ')}, & ${parts[parts.length - 1]} tax`;

  return <>{lineOne}</>;
};
